import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Download, PenLine, Plus, ReceiptText } from 'lucide-react';
import { Alert, Button, Icon, PageHeader, StatusPill } from '@/components/common';
import { useGstAccount } from '@/hooks/useGst';
import { comingSoon } from '@/utils/orderPresentation';
import { downloadInvoice, GST_EDIT_PATH, invoicePath } from '@/utils/gst';
import { formatDay, formatRupees } from '@/utils/productCases';

const COLUMN = 'tw-mx-auto tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-5 md:tw-gap-6';
const card = 'tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5';

const Tile: React.FC<{ icon: typeof Building2 }> = ({ icon }) => (
  <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-subtle tw-text-ds-text-secondary">
    <Icon icon={icon} size={20} />
  </span>
);

/** G4 GST invoices: saved GST details, tax invoices, and plans bought without a GSTIN. */
const GstInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { account, loading, error, reload } = useGstAccount();

  return (
    <div className={COLUMN}>
      <PageHeader title="GST invoices" subtitle="Tax invoices for plans bought with your GSTIN." />

      {loading ? (
        <div aria-busy className="tw-h-48 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
      ) : error ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <Alert tone="danger" title="Something went wrong">
            {error}
          </Alert>
          <Button variant="secondary" className="tw-self-start" onClick={reload}>
            Try again
          </Button>
        </div>
      ) : !account ? (
        <Alert title="GST invoices are coming soon">
          Soon you’ll be able to add your GSTIN and download tax invoices for your plans here.
        </Alert>
      ) : (
        <>
          <section aria-label="GST details" className={card}>
            <div className="tw-flex tw-items-center tw-gap-3">
              <Tile icon={Building2} />
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{account.profile?.legalName ?? 'No GSTIN added'}</p>
                <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                  {account.profile?.gstin ?? 'Add your GSTIN to get tax invoices for new plans'}
                </p>
              </div>
              {account.profile && <StatusPill tone="success">Verified</StatusPill>}
            </div>
            <Button variant="link" leadingIcon={account.profile ? PenLine : Plus} className="tw-self-start" onClick={() => navigate(GST_EDIT_PATH)}>
              {account.profile ? 'Edit GST details' : 'Add GST details'}
            </Button>
          </section>

          {account.invoices.length === 0 && (
            <p className="tw-text-[14px] tw-leading-5 tw-text-ds-text-secondary">No GST invoices yet. Plans you buy with your GSTIN get one here.</p>
          )}
          {account.invoices.map((invoice) => (
            <section key={invoice.id} aria-label={`Invoice ${invoice.number}`} className={`${card} tw-flex-row tw-items-center`}>
              <Tile icon={ReceiptText} />
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                <Link
                  to={invoicePath(invoice.id)}
                  className="tw-self-start tw-rounded tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
                >
                  {invoice.number}
                </Link>
                <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                  {[invoice.summary, formatDay(invoice.issuedAt), formatRupees(invoice.total)].join(' · ')}
                </p>
              </div>
              <Button size="sm" variant="secondary" leadingIcon={Download} onClick={() => downloadInvoice(invoice)} aria-label={`Download invoice ${invoice.number}`}>
                <span className="md:tw-hidden">PDF</span>
                <span className="tw-hidden md:tw-inline">Download</span>
              </Button>
            </section>
          ))}

          {account.withoutGst.length > 0 && (
            <>
              <Alert tone="warning" title="Bought a plan without your GSTIN?">
                You can add your GSTIN to a plan bought this month and we’ll reissue the invoice. Once the month closes, the invoice can’t be changed.
              </Alert>
              {account.withoutGst.map((plan) => (
                <section key={plan.id} aria-label={plan.title} className={`${card} tw-gap-4`}>
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <Tile icon={ReceiptText} />
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                      <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{plan.title}</p>
                      <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
                        Bought {formatDay(plan.boughtAt)} · no GSTIN · {formatRupees(plan.amount)}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="secondary" className="tw-self-start" onClick={() => comingSoon('Requesting a GST invoice')}>
                    Request GST invoice
                  </Button>
                </section>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GstInvoicesPage;
